import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {usePagination, useTable} from 'react-table';
import LoadingAnimation from '../BateryChargeAnimation';
import IfRender from '../IfRender';
import NoData from '../NoData';
import Pagination from './pagination';
import {CSVLink} from "react-csv";
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import './style.scss';

function ReactTable({
                        hiddenColumns = [],
                        data,
                        columns,
                        selectedRow = '',
                        setSelectedRow,
                        className = '',
                        loading = false,
                        //Providing these controlled props enables server side pagination
                        controlledPageCount = null,
                        setControlledPageSize = () => null,
                        setControlledPageIndex = () => null,
                        exportCsv = false,
                        csvData = []
                    }) {

    const controlledCount = controlledPageCount !== null ? {pageCount: controlledPageCount} : {};

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        setPageSize,
        pageOptions,
        pageCount,
        canPreviousPage,
        previousPage,
        canNextPage,
        nextPage,
        gotoPage,
        state: {pageIndex, pageSize}
    } = useTable(
        {
            columns,
            data,
            initialState: {
                hiddenColumns: ['_id', ...hiddenColumns],
                pageIndex: 0,
                pageSize: 10
            },
            manualPagination: controlledPageCount ? true : false,
            ...controlledCount
        },
        usePagination
    );

    return (
        <div className='react-table__padding'>
            <div className='react-table'>
                <IfRender condition={!loading} otherwise={<LoadingAnimation dark/>}>
                    <IfRender condition={data.length > 0} otherwise={<NoData/>}>
                        {exportCsv && <CSVLink className='download-btn' filename="sessions.csv" data={csvData}>
                            <FileDownloadIcon/>
                        </CSVLink>}
                        <table className={className} {...getTableProps()} cellPadding={0} cellSpacing={0}>
                            <thead>
                            {headerGroups.map((headerGroup, i) => (
                                <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column, j) => (
                                        <th
                                            {...column.getHeaderProps()}
                                            key={j}
                                            className={
                                                column.className === 'table-action-cell' ? 'table-action-cell' : ''
                                            }>
                                            {column.render('Header')}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>

                            <tbody {...getTableBodyProps()}>
                            {page.map((row, i) => {
                                const userId = row.original.id;
                                prepareRow(row);
                                return (
                                    <tr
                                        onClick={() =>
                                            setSelectedRow ? setSelectedRow(selectedRow === userId ? null : userId) : {}
                                        }
                                        {...row.getRowProps()}
                                        key={i}
                                        className={`${setSelectedRow ? 'selectable' : ''} ${selectedRow === userId ? 'selected' : ''
                                        }`}>
                                        {row.cells.map((cell, j) => {
                                            return (
                                                <td
                                                    {...cell.getCellProps()}
                                                    key={i + j}
                                                    className={cell.column.className}>
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </IfRender>
                </IfRender>
            </div>
            <Pagination
                length={data.length}
                pageSize={pageSize}
                setPageSize={(value) => {
                    setPageSize(value);
                    setControlledPageSize(value);
                }}
                pageOptions={pageOptions}
                pageCount={controlledPageCount || pageCount}
                canPreviousPage={canPreviousPage}
                previousPage={() => {
                    previousPage();
                    canPreviousPage && setControlledPageIndex(pageIndex - 1);
                }}
                canNextPage={canNextPage}
                nextPage={() => {
                    nextPage();
                    canNextPage && setControlledPageIndex(pageIndex + 1);
                }}
                gotoPage={(index) => {
                    gotoPage(index);
                    setControlledPageIndex(index);
                }}
                pageIndex={pageIndex}
            />
        </div>
    );
}

ReactTable.propTypes = {
    loading: PropTypes.bool,
    data: PropTypes.array,
    columns: PropTypes.array.isRequired,
    setSelectedRow: PropTypes.func,
    selectedRow: PropTypes.string,
    className: PropTypes.string,
    pageCount: PropTypes.number,
    controlledPageSize: PropTypes.number,
    setControlledPageSize: PropTypes.func,
    controlledPageIndex: PropTypes.number,
    setControlledPageindex: PropTypes.func,
    hiddenColumns: PropTypes.array
};

export default ReactTable;
